import { useEffect, useState, useRef, useCallback } from "react";
import { DataNotFound } from "../common/DataNotFound";
import clsx from "clsx";
import {
  BellOff,
  CalendarClock,
  ChevronRight,
  FileSignature,
  Banknote,
  ScrollText,
  ClipboardCheck,
  Gauge,
  Trash2,
  UserRound,
} from "lucide-react";
import { Tooltip } from "../common/Tooltip";
import { ConfirmModal } from "../common/ConfirmModal";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { uiLocale } from "../../utils/date/uiLocale";
import { useNotificationStore } from "../../store/notification.store";
import { NAV_ICONS } from "../icons/navIcons";
import { useFormatDate } from "../../utils/date/useFormatDate";
import { getCurrentUser } from "../../auth/auth.service";
import { toast } from "../common/toast";

// The notification shape returned by the store is loosely typed upstream —
// aliased once here so the link/copy/open-in-new-tab helpers below don't
// each redeclare their own `any` (and multiply the existing lint debt).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type NotificationLike = any;

// Matches Emeis.Notifying.Domain NotificationType.EntityDeleted — a deleted
// master-data record (business unit, department, contract type, etc.) has
// no reliable detail-page destination to route to, so these are handled as
// an inline message instead of a navigation.
const NOTIFICATION_TYPE_ENTITY_DELETED = 8;

const formatNotificationMessage = (msg?: string) => {
  if (!msg) return "";

  if (msg.startsWith("{") && msg.endsWith("}")) {
    try {
      const normalizedJson = msg.replace(/'/g, '"');
      const parsed = JSON.parse(normalizedJson);

      const fullName =
        `${parsed.FirstName || ""} ${parsed.LastName || ""}`.trim();
      if (fullName) {
        return i18n.t("notifications.profileUpdated", {
          ns: "topbar",
          name: fullName,
          email: parsed.Email || "",
        });
      }
    } catch {
      return msg;
    }
  }

  return msg;
};

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

// Compact time for a dropdown row: "5 min ago" today, "Yesterday", then a
// short date. The full timestamp stays in the tooltip.
const shortTime = (value: string) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diff = Math.round((d.getTime() - now.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(uiLocale(), {
    numeric: "auto",
    style: "short",
  });
  if (dayKey(d) === dayKey(now)) {
    if (Math.abs(diff) < 60) return rtf.format(0, "second");
    if (Math.abs(diff) < 3600)
      return rtf.format(Math.round(diff / 60), "minute");
    return rtf.format(Math.round(diff / 3600), "hour");
  }
  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (dayKey(d) === dayKey(y)) return rtf.format(-1, "day");
  return d.toLocaleDateString(uiLocale(), {
    day: "numeric",
    month: "short",
    year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
};

export function NotificationPanel() {
  const navigate = useNavigate();
  // Only the mark-all-read toasts are translated here — the same strings the
  // Notifications page shows, so the bell and the page don't answer the same
  // action in two different languages.
  const { t } = useTranslation("notifications");
  // The panel's own words live in the topbar namespace.
  const { t: tt } = useTranslation("topbar");
  const open = useNotificationStore((state) => state.open);
  const notifications = useNotificationStore((state) => state.notifications);
  const loading = useNotificationStore((state) => state.loading);
  const hasMore = useNotificationStore((state) => state.hasMore);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const markRead = useNotificationStore((state) => state.markRead);
  const markAllRead = useNotificationStore((state) => state.markAllRead);
  const toggle = useNotificationStore((state) => state.toggle);
  const fetchNotifications = useNotificationStore(
    (state) => state.fetchNotifications,
  );
  const fetchUnreadCount = useNotificationStore(
    (state) => state.fetchUnreadCount,
  );

  const formatDate = useFormatDate();
  const InboxIcon = NAV_ICONS.inbox;

  const [currentUser, setCurrentUser] = useState<Awaited<
    ReturnType<typeof getCurrentUser>
  > | null>(null);

  // Same guard as the full Notifications page: the bell's button clears the
  // whole inbox too, so it asks the same question before it does.
  const [confirmMarkAllRead, setConfirmMarkAllRead] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUser(user);
      } catch (err) {
        console.error("Failed to fetch current user:", err);
      }
    };
    fetchUser();
  }, []);

  const observer = useRef<IntersectionObserver | null>(null);

  const lastNotificationRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchNotifications(false);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, fetchNotifications],
  );

  useEffect(() => {
    if (open) {
      fetchNotifications(true);
      fetchUnreadCount();
    }
  }, [open, fetchNotifications, fetchUnreadCount]);

  if (!open) return null;

  // Resolves the in-app SPA path a notification should open — shared by the
  // row click, "Open in new tab", and "Copy link".
  const getTargetPath = (n: NotificationLike): string | null => {
    const type = Number(n.notificationType ?? n.type);

    // Deleted-record notifications have no detail page to open — shown as
    // a message instead (see handleNotificationClick / handleViewLinkClick).
    if (type === NOTIFICATION_TYPE_ENTITY_DELETED) {
      return null;
    }

    let extractedId: string | null = null;
    let cleanUrlStr = n.deepLinkUrl ? n.deepLinkUrl.trim() : "";

    if (cleanUrlStr) {
      if (
        cleanUrlStr.startsWith("http:/") &&
        !cleanUrlStr.startsWith("http://")
      ) {
        cleanUrlStr = cleanUrlStr.replace("http:/", "http://");
      } else if (
        cleanUrlStr.startsWith("https:/") &&
        !cleanUrlStr.startsWith("https://")
      ) {
        cleanUrlStr = cleanUrlStr.replace("https:/", "https://");
      }

      try {
        if (
          cleanUrlStr.startsWith("http://") ||
          cleanUrlStr.startsWith("https://")
        ) {
          const parsedUrl = new URL(cleanUrlStr);
          extractedId = parsedUrl.searchParams.get("id");
        }
      } catch (err) {
        console.error("Error parsing deepLinkUrl:", err);
      }
    }

    // Type 4 -> Contract Created, 5 -> Contract Workflow Sent, 6 -> Contract
    // Workflow Decision, 7 -> Renewal Reminder — all route to the same
    // Contract Details page.
    // Licensing: a usage alert (9) or an upgrade request from a member (10)
    // is acted on from the subscription page, whoever it is about.
    if (type === 9 || type === 10) {
      return "/admin/subscription";
    }

    // 11 -> anything about an obligation. Opens that obligation; without an
    // id it falls back to the inbox, which lists whatever is waiting.
    if (type === 11) {
      return extractedId && extractedId.trim() !== ""
        ? `/obligations-hub/${extractedId}`
        : "/obligations-hub/my-work";
    }

    // 12 -> anything about a Receivables Engine entry. Opens that entry;
    // without an id it falls back to the list of entries.
    if (type === 12) {
      return extractedId && extractedId.trim() !== ""
        ? `/receivables-engine/${extractedId}`
        : "/receivables-engine/all";
    }

    // 13 -> anything about a Clause Library clause. Opens that clause;
    // without an id it falls back to the library.
    if (type === 13) {
      return extractedId && extractedId.trim() !== ""
        ? `/library/clause-templates/${extractedId}`
        : "/library/clause-templates/all";
    }

    // 14 -> a section-level review inside a contract. The link is
    // "/contracts/review/<assignment>?id=<contract>"; the page opens that
    // thread and scrolls to the passage via "?review=".
    if (type === 14) {
      const review = cleanUrlStr.match(/\/contracts\/review\/([0-9a-f-]{36})/i)?.[1];
      const comment = cleanUrlStr.match(/\/contracts\/comment\/([0-9a-f-]{36})/i)?.[1];
      const targetContractId =
        extractedId && extractedId.trim() !== "" ? extractedId : n.id;
      const query = review ? `?review=${review}` : comment ? `?comment=${comment}` : "";
      return targetContractId ? `/contracts/${targetContractId}${query}` : null;
    }

    // 15 -> Pactline Native Signature. A signer's turn opens the signing
    // ceremony ("/contracts/sign/<request>?id=<contract>"); anything else
    // about the request opens the contract.
    if (type === 15) {
      const signing = cleanUrlStr.match(/\/contracts\/sign\/([0-9a-f-]{36})/i)?.[1];
      if (signing) return `/contracts/sign/${signing}`;
      const targetContractId =
        extractedId && extractedId.trim() !== "" ? extractedId : n.id;
      return targetContractId ? `/contracts/${targetContractId}` : null;
    }

    if (type === 4 || type === 5 || type === 6 || type === 7) {
      const targetContractId =
        extractedId && extractedId.trim() !== "" ? extractedId : n.id;
      return targetContractId ? `/contracts/${targetContractId}` : null;
    }

    // All other Types -> Staff Details
    const targetStaffId =
      extractedId && extractedId.trim() !== ""
        ? extractedId
        : n.userId || currentUser?.id;
    return targetStaffId ? `/admin/staff/${targetStaffId}` : null;
  };

  const markReadLocally = (n: NotificationLike) => {
    if (!n.isRead) {
      markRead(n.id);
    }
  };

  const showDeletedEntityMessage = (n: NotificationLike) => {
    toast.success(
      n.message || tt("notifications.recordDeleted"),
    );
  };

  const handleNotificationClick = (
    e: React.MouseEvent,
    n: NotificationLike,
  ) => {
    e.stopPropagation();
    markReadLocally(n);
    toggle();
    const type = Number(n.notificationType ?? n.type);
    if (type === NOTIFICATION_TYPE_ENTITY_DELETED) {
      showDeletedEntityMessage(n);
      return;
    }
    const path = getTargetPath(n);
    if (path) {
      navigate(path);
    }
  };

  // Left-click keeps SPA client-side routing (no full reload); a modified
  // click (ctrl/cmd/middle/right) is left alone so the browser's native
  // "open in new tab" on the real href still works.
  const handleViewLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    n: NotificationLike,
  ) => {
    e.stopPropagation();
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    markReadLocally(n);
    toggle();
    const type = Number(n.notificationType ?? n.type);
    if (type === NOTIFICATION_TYPE_ENTITY_DELETED) {
      showDeletedEntityMessage(n);
      return;
    }
    const path = getTargetPath(n);
    if (path) navigate(path);
  };

  const handleCopyLink = async (e: React.MouseEvent, n: NotificationLike) => {
    e.stopPropagation();
    const path = getTargetPath(n);
    if (!path) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${path}`);
      toast.success(tt("notifications.linkCopied"));
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error(tt("notifications.copyFailed"));
    }
  };

  const handleOpenInNewTab = (e: React.MouseEvent, n: NotificationLike) => {
    e.stopPropagation();
    const path = getTargetPath(n);
    if (!path) return;
    markReadLocally(n);
    window.open(
      `${window.location.origin}${path}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  // Safe check for enabled button state
  const hasUnreadItems =
    unreadCount > 0 || notifications.some((n) => !n.isRead);

  return (
    <div
      className="
        fixed top-16 right-4 z-50
        w-[92vw]
        sm:w-[420px]
        overflow-hidden
        rounded-2xl
        border border-[rgb(var(--border))]
        bg-[rgb(var(--card))]
        shadow-2xl shadow-slate-900/15
        backdrop-blur-xl
      "
    >
      {/* Header Section */}
      <div
        className="
          flex justify-between items-center
          px-4 py-3
          border-b border-[rgb(var(--border))] bg-[rgb(var(--card-muted))]
        "
      >
        <div
          className="
            flex items-center gap-2
            font-semibold text-sm
            text-[rgb(var(--text-primary))]
          "
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[rgb(var(--text-accent))]/10 text-[rgb(var(--text-accent))]">
            <InboxIcon size={17} />
          </span>
          <span>{tt("notifications.title")}</span>
          {unreadCount > 0 && (
            <span className="rounded-full bg-[rgb(var(--accent-fill))] px-2 py-0.5 text-[11px] font-bold text-[rgb(var(--accent-ink))]">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setConfirmMarkAllRead(true)}
            disabled={!hasUnreadItems}
            className="
            inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg
            text-[rgb(var(--text-accent))] font-semibold
            bg-[rgb(var(--card))]
            hover:bg-[rgb(var(--accent-fill))] hover:text-[rgb(var(--accent-ink))]
            border border-[rgb(var(--border))]
            transition-all duration-200
            disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent
          "
          >
            <NAV_ICONS.tick size={14} />
            {tt("notifications.markAllRead")}
          </button>
          <button
            type="button"
            onClick={() => toggle()}
            aria-label={tt("notifications.close")}
            title={tt("notifications.closeShort")}
            className="
            inline-flex h-8 w-8 items-center justify-center rounded-lg
            text-[rgb(var(--text-muted))]
            hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--text-primary))]
            transition-colors duration-200
          "
          >
            <NAV_ICONS.close size={16} />
          </button>
        </div>
      </div>

      {/* Notifications List Section */}
      <div
        className="
          max-h-[min(500px,calc(100vh-12rem))]
          overflow-y-auto overflow-x-visible
        "
      >
        {loading && notifications.length === 0 ? (
          <div className="py-12 text-center text-sm text-[rgb(var(--text-muted))]">
            {tt("notifications.loading")}
          </div>
        ) : !notifications.length ? (
          <DataNotFound
            compact
            bare
            icon={BellOff}
            title={tt("notifications.empty")}
            description={null}
          />
        ) : (
          <>
            {notifications.map((n, index) => {
              const isLastItem = notifications.length === index + 1;
              const type = Number(
                (n as NotificationLike).notificationType ??
                  (n as NotificationLike).type,
              );
              const tone =
                type === NOTIFICATION_TYPE_ENTITY_DELETED ||
                type === 7 ||
                n.isUrgent
                  ? "red"
                  : type === 9 || type === 10
                    ? "amber"
                    : "accent";
              const Icon =
                type === NOTIFICATION_TYPE_ENTITY_DELETED
                  ? Trash2
                  : type === 7
                    ? CalendarClock
                    : type === 9 || type === 10
                      ? Gauge
                      : type === 11
                        ? ClipboardCheck
                        : type === 12
                        ? Banknote
                        : type === 13
                        ? ScrollText
                        : type === 4 || type === 5 || type === 6 || type === 15
                          ? FileSignature
                          : UserRound;
              const target = n.deepLinkUrl ? getTargetPath(n) : null;
              const unread = !n.isRead;

              return (
                <div
                  key={n.id}
                  ref={isLastItem ? lastNotificationRef : null}
                  onClick={(e) => handleNotificationClick(e, n)}
                  className={clsx(
                    "group relative flex cursor-pointer gap-3 border-b border-[rgb(var(--border))] px-4 py-3 transition-colors last:border-b-0 hover:bg-[rgb(var(--card-muted))]",
                    unread && "bg-[rgb(var(--text-accent))]/[0.04]",
                  )}
                >
                  {unread && (
                    <span
                      aria-hidden
                      className={clsx(
                        "absolute inset-y-2 left-0 w-[3px] rounded-r-full",
                        tone === "red"
                          ? "bg-red-500"
                          : "bg-[rgb(var(--accent-fill))]",
                      )}
                    />
                  )}
                  <div
                    className={clsx(
                      "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                      tone === "red" &&
                        "bg-red-500/10 text-red-600 dark:text-red-400",
                      tone === "amber" &&
                        "bg-amber-500/10 text-amber-700 dark:text-amber-400",
                      tone === "accent" &&
                        (unread
                          ? "bg-[rgb(var(--text-accent))]/15 text-[rgb(var(--text-accent))]"
                          : "bg-[rgb(var(--card-muted))] text-[rgb(var(--text-muted))]"),
                    )}
                  >
                    <Icon size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <Tooltip label={n.title} display="contents">
                        <div
                          className={clsx(
                            "truncate text-[13px] text-[rgb(var(--text-primary))]",
                            unread ? "font-semibold" : "font-medium",
                          )}
                        >
                          {n.title}
                        </div>
                      </Tooltip>
                      <Tooltip label={formatDate(n.creationDate)}>
                        <span className="shrink-0 whitespace-nowrap text-[11px] tabular-nums text-[rgb(var(--text-muted))]">
                          {shortTime(n.creationDate)}
                        </span>
                      </Tooltip>
                    </div>

                    {n.message && (
                      <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-[rgb(var(--text-secondary))]">
                        {formatNotificationMessage(n.message)}
                      </p>
                    )}

                    {target && (
                      <div className="mt-1 flex items-center gap-0.5 sm:pointer-events-none sm:absolute sm:bottom-1.5 sm:right-2 sm:mt-0 sm:rounded-lg sm:border sm:border-[rgb(var(--border))] sm:bg-[rgb(var(--card))] sm:px-0.5 sm:opacity-0 sm:shadow-sm sm:transition-opacity sm:group-hover:pointer-events-auto sm:group-hover:opacity-100 sm:group-focus-within:pointer-events-auto sm:group-focus-within:opacity-100">
                        <a
                          href={`${window.location.origin}${target}`}
                          onClick={(e) => handleViewLinkClick(e, n)}
                          className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-semibold text-[rgb(var(--text-accent))] hover:bg-[rgb(var(--text-accent))]/10"
                        >
                          {tt("notifications.view")}
                          <ChevronRight size={12} />
                        </a>
                        <Tooltip label={tt("notifications.openNewTab")}>
                          <button
                            type="button"
                            onClick={(e) => handleOpenInNewTab(e, n)}
                            aria-label={tt("notifications.openNewTab")}
                            className="rounded-md p-1 text-[rgb(var(--text-muted))] hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--text-accent))]"
                          >
                            <NAV_ICONS.openInNewTab size={12} />
                          </button>
                        </Tooltip>
                        <Tooltip label={tt("notifications.copyLink")}>
                          <button
                            type="button"
                            onClick={(e) => handleCopyLink(e, n)}
                            aria-label={tt("notifications.copyLink")}
                            className="rounded-md p-1 text-[rgb(var(--text-muted))] hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--text-accent))]"
                          >
                            <NAV_ICONS.copyLink size={12} />
                          </button>
                        </Tooltip>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Bottom Loader Indicators */}
            {loading && (
              <div className="py-3 text-center text-xs text-[rgb(var(--text-muted))] font-medium">
                Loading more notifications...
              </div>
            )}

            {!hasMore && notifications.length > 0 && (
              <div className="py-3 text-center text-xs text-[rgb(var(--text-muted))] italic">
                You've caught up with all notifications.
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-[rgb(var(--border))] bg-[rgb(var(--card-muted))] px-2 py-1.5">
        <button
          type="button"
          onClick={() => {
            toggle();
            navigate("/notifications");
          }}
          className="flex w-full items-center justify-center gap-1 rounded-lg py-2 text-xs font-semibold text-[rgb(var(--text-accent))] transition-colors hover:bg-[rgb(var(--text-accent))]/10"
        >
          View all notifications
          <ChevronRight size={14} />
        </button>
      </div>

      <ConfirmModal
        open={confirmMarkAllRead}
        variant="info"
        title={tt("notifications.markAllRead")}
        description={tt("notifications.markAllReadBody", {
          count: Math.max(
            unreadCount,
            notifications.filter((n) => !n.isRead).length,
          ),
        })}
        confirmLabel={tt("notifications.markAllRead")}
        onConfirm={async () => {
          try {
            await markAllRead();
            toast.success(t("toast.markAllReadSuccess"));
          } catch (err) {
            console.error("❌ Failed to mark all notifications read:", err);
            toast.error(t("toast.markAllReadError"));
          } finally {
            setConfirmMarkAllRead(false);
          }
        }}
        onCancel={() => setConfirmMarkAllRead(false)}
      />
    </div>
  );
}
